import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Link,
  useNavigate
} from 'react-router-dom';

import './AdminCourses.scss';

export default function AdminCourses() {
  const navigate = useNavigate();

  const apiBaseUrl = useMemo(() => {
    const envUrl =
      process.env.REACT_APP_API_URL ||
      '';

    return envUrl.endsWith('/')
      ? envUrl.slice(0, -1)
      : envUrl;
  }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    courses,
    setCourses
  ] = useState([]);

  const [
    search,
    setSearch
  ] = useState('');

  const [
    province,
    setProvince
  ] = useState('all');

  const [
    courseType,
    setCourseType
  ] = useState('all');

  const [
    holes,
    setHoles
  ] = useState('all');

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState('');

  useEffect(() => {
    if (!token) {
      return;
    }

    const fetchCourses =
      async () => {
        setLoading(true);
        setError('');

        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/courses`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load courses.'
            );
          }

          setCourses(
            data.courses || []
          );
        } catch (err) {
          setError(
            err.message ||
              'Unable to load courses.'
          );
        } finally {
          setLoading(false);
        }
      };

    fetchCourses();
  }, [
    apiBaseUrl,
    token
  ]);

  const availableProvinces =
    useMemo(() => {
      return [
        ...new Set(
          courses
            .map(
              (course) =>
                course.province
            )
            .filter(Boolean)
        )
      ].sort();
    }, [courses]);

  const availableTypes =
    useMemo(() => {
      return [
        ...new Set(
          courses
            .map(
              (course) =>
                course.public_private
            )
            .filter(Boolean)
        )
      ].sort();
    }, [courses]);

  const filteredCourses =
    useMemo(() => {
      const normalized =
        search
          .trim()
          .toLowerCase();

      return courses.filter(
        (course) => {
          const matchesSearch =
            !normalized ||
            course.course_name
              ?.toLowerCase()
              .includes(normalized) ||
            course.city
              ?.toLowerCase()
              .includes(normalized) ||
            course.address
              ?.toLowerCase()
              .includes(normalized);

          const matchesProvince =
            province === 'all' ||
            course.province ===
              province;

          const matchesType =
            courseType === 'all' ||
            course.public_private ===
              courseType;

          const matchesHoles =
            holes === 'all' ||
            Number(
              course.num_holes
            ) === Number(holes);

          return (
            matchesSearch &&
            matchesProvince &&
            matchesType &&
            matchesHoles
          );
        }
      );
    }, [
      courses,
      search,
      province,
      courseType,
      holes
    ]);

  const coursesWithGps =
    courses.filter(
      (course) =>
        course.latitude &&
        course.longitude
    ).length;

  const coursesWithWebsite =
    courses.filter(
      (course) =>
        course.course_website_link
    ).length;

  return (
    <div className="admin-courses-page">
      <section className="admin-courses-heading">
        <div>
          <p className="admin-courses-heading__eyebrow">
            Course Network
          </p>

          <h2>
            Courses
          </h2>

          <p>
            Add, edit and maintain
            The Tour Golfers course
            database.
          </p>
        </div>

        <Link
          to="/admin/courses/new"
          className="admin-courses-heading__button"
        >
          <span>
            +
          </span>

          Add Course
        </Link>
      </section>

      <section className="admin-course-summary">
        <div className="admin-course-summary__card">
          <span>
            Total Courses
          </span>

          <strong>
            {courses.length}
          </strong>
        </div>

        <div className="admin-course-summary__card">
          <span>
            18 Hole Courses
          </span>

          <strong>
            {
              courses.filter(
                (course) =>
                  Number(
                    course.num_holes
                  ) === 18
              ).length
            }
          </strong>
        </div>

        <div className="admin-course-summary__card">
          <span>
            GPS Location
          </span>

          <strong>
            {coursesWithGps}
          </strong>
        </div>

        <div className="admin-course-summary__card">
          <span>
            Website Added
          </span>

          <strong>
            {coursesWithWebsite}
          </strong>
        </div>
      </section>

      <section className="admin-course-filters">
        <div className="admin-course-filters__search">
          <label htmlFor="courseSearch">
            Search Courses
          </label>

          <input
            id="courseSearch"
            type="text"
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Search course, city or address..."
          />
        </div>

        <div className="admin-course-filters__field">
          <label htmlFor="provinceFilter">
            Province
          </label>

          <select
            id="provinceFilter"
            value={province}
            onChange={(event) =>
              setProvince(
                event.target.value
              )
            }
          >
            <option value="all">
              All Provinces
            </option>

            {availableProvinces.map(
              (item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        <div className="admin-course-filters__field">
          <label htmlFor="typeFilter">
            Course Type
          </label>

          <select
            id="typeFilter"
            value={courseType}
            onChange={(event) =>
              setCourseType(
                event.target.value
              )
            }
          >
            <option value="all">
              All Types
            </option>

            {availableTypes.map(
              (item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>
        </div>

        <div className="admin-course-filters__field">
          <label htmlFor="holesFilter">
            Holes
          </label>

          <select
            id="holesFilter"
            value={holes}
            onChange={(event) =>
              setHoles(
                event.target.value
              )
            }
          >
            <option value="all">
              All
            </option>

            <option value="9">
              9
            </option>

            <option value="18">
              18
            </option>

            <option value="27">
              27
            </option>

            <option value="36">
              36
            </option>
          </select>
        </div>
      </section>

      {error ? (
        <div className="admin-courses-error">
          {error}
        </div>
      ) : null}

      <section className="admin-courses-table-card">
        <div className="admin-courses-table-card__header">
          <div>
            <h3>
              Course Database
            </h3>

            <span>
              {
                filteredCourses.length
              }{' '}
              results
            </span>
          </div>
        </div>

        {loading ? (
          <div className="admin-courses-empty">
            Loading courses...
          </div>
        ) : filteredCourses.length ===
          0 ? (
          <div className="admin-courses-empty">
            No courses match the
            current filters.
          </div>
        ) : (
          <div className="admin-courses-table-wrapper">
            <table className="admin-courses-table">
              <thead>
                <tr>
                  <th>
                    Course
                  </th>

                  <th>
                    Location
                  </th>

                  <th>
                    Holes
                  </th>

                  <th>
                    Type
                  </th>

                  <th>
                    GPS
                  </th>

                  <th>
                    Website
                  </th>

                  <th>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredCourses.map(
                  (course) => (
                    <tr
                      key={
                        course.course_id
                      }
                    >
                      <td>
                        <div className="admin-course-name">
                          <strong>
                            {
                              course.course_name
                            }
                          </strong>

                          <span>
                            ID:{' '}
                            {
                              course.course_id
                            }
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="admin-course-location">
                          <strong>
                            {course.city ||
                              '—'}
                          </strong>

                          <span>
                            {course.province ||
                              '—'}
                          </span>
                        </div>
                      </td>

                      <td>
                        {course.num_holes ||
                          '—'}
                      </td>

                      <td>
                        {course.public_private ||
                          '—'}
                      </td>

                      <td>
                        {course.latitude &&
                        course.longitude
                          ? 'Yes'
                          : 'No'}
                      </td>

                      <td>
                        {course.course_website_link
                          ? 'Yes'
                          : 'No'}
                      </td>

                      <td>
                        <button
                          type="button"
                          className="admin-course-edit-button"
                          onClick={() =>
                            navigate(
                              `/admin/courses/${course.course_id}`
                            )
                          }
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}